import React from 'react';
import { Box, Switch, TextField, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { isValidClippingAssetUrl } from './ugcCampaignFormHelpers';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';

const CLIPPING_FOLDER_HINT =
    'Upload your files in one shared folder: put footage under a footage folder, music under music, and any other files under extras. The URL must be publicly accessible so anyone with the link can download.';

export interface ClippingCampaignToggleProps {
    checked: boolean;
    assetUrl: string;
    onCheckedChange: (checked: boolean) => void;
    onAssetUrlChange: (url: string) => void;
    fieldSx?: Record<string, unknown>;
    labelSx?: Record<string, unknown>;
}

export function ClippingCampaignToggle({
    checked,
    assetUrl,
    onCheckedChange,
    onAssetUrlChange,
    fieldSx,
    labelSx,
}: ClippingCampaignToggleProps) {
    const urlInvalid = Boolean(assetUrl.trim()) && !isValidClippingAssetUrl(assetUrl);

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                border: checked ? `1px solid ${BRAND_LUXE_PRIMARY}55` : `1px solid transparent`,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 17,
                            color: '#fff',
                            letterSpacing: 0.2,
                        }}>
                        Clipping campaign
                    </Typography>
                    <Typography
                        sx={{
                            mt: 0.75,
                            fontSize: 14,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: 'rgba(255,255,255,0.72)',
                            lineHeight: 1.55,
                        }}>
                        Use this when creators cut your raw source package into short clips for social. You provide the
                        footage package; they edit, publish, and submit their live links.
                    </Typography>
                </Box>
                <Switch
                    size="small"
                    checked={checked}
                    onChange={(_, v) => onCheckedChange(v)}
                    inputProps={{ 'aria-label': 'Clipping campaign' }}
                    sx={{
                        flexShrink: 0,
                        mt: 0.25,
                        '& .MuiSwitch-switchBase.Mui-checked': {
                            color: BRAND_LUXE_SECONDARY,
                            '& + .MuiSwitch-track': { backgroundColor: 'rgba(0,108,80,0.55)', opacity: 1 },
                        },
                        '& .MuiSwitch-track': { opacity: 1, backgroundColor: BRAND_OUTLINE_VARIANT },
                    }}
                />
            </Box>

            {checked ? (
                <Box>
                    <Typography
                        sx={
                            labelSx ?? {
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'uppercase',
                                letterSpacing: '0.14em',
                                color: 'rgba(255,255,255,0.55)',
                                mb: 1,
                            }
                        }>
                        Source package link
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="https://drive.google.com/… or Dropbox link"
                        value={assetUrl}
                        onChange={e => onAssetUrlChange(e.target.value)}
                        error={urlInvalid}
                        helperText={urlInvalid ? 'Enter a valid http(s) link' : CLIPPING_FOLDER_HINT}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: urlInvalid ? undefined : 'rgba(255,255,255,0.5)',
                                mt: 1.25,
                                lineHeight: 1.55,
                            },
                        }}
                        sx={{
                            ...(fieldSx || {
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                    borderRadius: 2,
                                    color: '#fff',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                                },
                            }),
                            '& .MuiInputBase-input': {
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                                color: '#fff',
                            },
                        }}
                    />
                </Box>
            ) : null}
        </Box>
    );
}
